import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import Form from 'components/form-multi/Form';
import { AMPLITUDE_API_KEY } from 'components/utils/AmplitudeUtils';

import { toggleModalWithTimer, toggleSpinner } from 'actions/appAction';
import { toggleChat } from 'actions/chatAction';
import { addExtras, setExtra } from 'actions/extraAction';
import { updatePageMessages } from 'actions/messageAction';

import { OnfidoService } from 'services/OnfidoService';

import {
    AF_RECIPIENT_EMAIL_CONTINUE_CLICKED,
    AF_RECIPIENT_IDENTITY_REQUEST_PROMO_CONTINUE_CLICKED,
    AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED,
    AF_RECIPIENT_PERSONAL_DATA_PROCESSING_AGREE_CLICKED,
} from 'constants/amplitude/AmplitudeEvents';
import { SCENARIO_ID } from 'constants/amplitude/AmplitudeParamName';
import { KYC_FAILED_SCREEN, KYC_VERIFYING_SCREEN, ONFIDO_DOCUMENT_EXPLANATION_SCREEN, ONFIDO_DOCUMENTS_SCREEN, ONFIDO_SDK } from 'constants/ComponentNames';
import { ERROR } from 'constants/Constants';
import { LANDING_LINK } from 'constants/UrlConstants';

import { apiFetchData } from 'helpers/api';
import { addOnfido } from 'helpers/DOM';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { useExtra, useForm, usePageMessages } from 'hooks/selectors';
import { OnfidoContainer } from 'pages-auth/kyc/OnfidoFramePage';

import { CommonPagePromo } from '../common/CommonPagePromo';
import { LightButton } from '../common/fromAuth/LightButton/LightButton';
import { LightDangerouslyHtml } from '../common/fromAuth/LightDangerouslyHtml/LightDangerouslyHtml';
import { LightFixedBottomPanel } from '../common/fromAuth/LightFixedBottomPanel/LightFixedBottomPanel';
import { LightHeaderPanel } from '../common/fromAuth/LightHeaderPanel/LightHeaderPanel';
import { LightMarkedList } from '../common/fromAuth/LightMarkedList/LightMarkedList';
import { LightMarkedListItem } from '../common/fromAuth/LightMarkedList/LightMarkedListItem';
import LightOnfidoDocList from '../common/fromAuth/LightOnfidoDocList/LightOnfidoDocList';
import { PromoInfoPage } from '../common/PromoInfoPage/PromoInfoPage';
import PromoInputText from '../form/PromoInputText';

const AntiFraudRecipientFlowPage = (props) => {
    const INTRO_SCREEN = 'INTRO_SCREEN';
    const AGREEMENT_SCREEN = 'AGREEMENT_SCREEN';
    const EMAIL_SCREEN = 'EMAIL_SCREEN';
    const EXPIRED_SCREEN = 'EXPIRED_SCREEN';
    const ANTIFRAUD_SCENARIO_ID = 1000;

    const extra = useExtra();

    const getFirstScreen = () => {
        if (extra.expired) {
            return EXPIRED_SCREEN;
        } else if (extra.fraudOnfidoInProgress) {
            return KYC_VERIFYING_SCREEN;
        } else if (extra.fraudOnfidoNeedToRetry || extra.fraudOnfidoTooManyAttempts) {
            return KYC_FAILED_SCREEN;
        } else {
            return INTRO_SCREEN;
        }
    };

    const onfidoOutRef = useRef(null);
    const [showOnfido, setShowOnfido] = useState(false);
    const [screen, setScreen] = useState(getFirstScreen());
    const messages = usePageMessages();
    const paymentInfo = extra.paymentInfo;
    const form = useForm('recipientEmail');

    useEffect(() => {
        if (__isProd__ && __isBrowser__) {
            amplitude.getInstance().init(AMPLITUDE_API_KEY, null, {
                serverZone: 'EU',
                serverZoneBasedApi: true,
            });
        } else {
            amplitude.getInstance = () => {
                return {
                    setUserId: () => {},
                    setUserProperties: () => {},
                    logEvent: () => {},
                };
            };
        }
    }, []);

    useEffect(() => {
        if (showOnfido && typeof window !== 'undefined') {
            if (typeof window.Onfido === 'undefined') {
                addOnfido(() => {
                    initOnfido();
                });
            } else {
                initOnfido();
            }
        }
        return () => {
            onfidoOutRef.current?.tearDown();
        };
    }, [showOnfido]);

    const initOnfido = () => {
        let options = OnfidoService.getRecipientFraudOptions(extra.docInfo, extra.docs);

        onfidoOutRef.current = OnfidoService.init(window.Onfido, extra.onfidoAccessToken, options, onComplete, onError, 'light');
    };

    const openOnfido = () => {
        setScreen(ONFIDO_SDK);
        setShowOnfido(true);
    };

    const onComplete = () => {
        apiFetchData(`/antifraud/onfido/recipient/uploaded/document`, getRecipientFieldsForOnfido());

        props.toggleModalWithTimer(true, messages.submittedSpinner, () => {
            setScreen(KYC_VERIFYING_SCREEN);
        });
    };

    const onError = (data) => {
        Sentry.captureMessage('Recipient Onfido Frame error', {
            extra: {
                countryId: extra.paymentInfo?.recipientCountry,
                data: data,
            },
            level: 'error',
        });

        setScreen(KYC_FAILED_SCREEN);
    };

    const onBackIconClick = (e) => {
        e.preventDefault();
        setScreen(INTRO_SCREEN);
    };

    const agreementButtonClick = (e) => {
        e.preventDefault();
        amplitude.getInstance().logEvent(AF_RECIPIENT_PERSONAL_DATA_PROCESSING_AGREE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });
        setScreen(EMAIL_SCREEN);
    };

    const onClickContinueOnIntro = (e) => {
        e.preventDefault();
        amplitude.getInstance().logEvent(AF_RECIPIENT_IDENTITY_REQUEST_PROMO_CONTINUE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });

        if (extra.docs) {
            setScreen(ONFIDO_DOCUMENTS_SCREEN);
        } else if (extra.recipientSumsubEnabled && extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = extra.recipientAntiFraudStatus?.url;
        } else {
            setScreen(AGREEMENT_SCREEN);
        }
    };

    const onClickBackIcon = (e) => {
        e.preventDefault();

        switch (screen) {
            case AGREEMENT_SCREEN:
                setScreen(INTRO_SCREEN);
                break;
            case EMAIL_SCREEN:
                setScreen(AGREEMENT_SCREEN);
                break;
            case ONFIDO_SDK:
                setShowOnfido(false);
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
                break;
            case ONFIDO_DOCUMENT_EXPLANATION_SCREEN:
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
                break;
            case ONFIDO_DOCUMENTS_SCREEN:
                setScreen(extra.fraudOnfidoNeedToRetry ? KYC_FAILED_SCREEN : INTRO_SCREEN);
                break;
            default:
                return null;
        }
    };

    const submitEmailHandler = (fields, json) => {
        amplitude.getInstance().logEvent(AF_RECIPIENT_EMAIL_CONTINUE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });

        if (json.extra.docs) {
            new Promise((resolve) => resolve(props.addExtras(json.extra))).then(() => {
                setScreen(ONFIDO_DOCUMENTS_SCREEN);
            });
        } else if (json.extra?.recipientSumsubEnabled && json.extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = json.extra.recipientAntiFraudStatus?.url;
        }
    };

    const getRecipientFieldsForOnfido = () => {
        return [
            {
                name: 'uuid',
                value: paymentInfo.uuid,
            },
            {
                name: 'ticketId',
                value: paymentInfo.ticketId,
            },
            {
                name: 'senderBalId',
                value: paymentInfo.senderBalId,
            },
            {
                name: 'toName',
                value: paymentInfo.toName,
            },
            {
                name: 'toNameF',
                value: paymentInfo.toNameF,
            },
            {
                name: 'recipientCountry',
                value: paymentInfo.recipientCountry,
            },
            {
                name: 'scenarioId',
                value: paymentInfo.scenarioId,
            },
        ];
    };

    const onClickDocument = (docType) => {
        props.toggleSpinner(true);

        apiFetchData(`/antifraud/recipient/onfido/get/document/${docType}`, getRecipientFieldsForOnfido())
            .then((json) => {
                if (json) {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.addExtras(json.extra);
                    }
                }
                props.toggleSpinner(false);
                setScreen(ONFIDO_DOCUMENT_EXPLANATION_SCREEN);
            })
            .catch((e) => {
                console.log(e);
                props.toggleSpinner(false);
                setScreen(ERROR);
            });
    };

    const openChat = (e) => {
        e.preventDefault();
        props.toggleChat(true);
    };

    const clickOnFailedOnfidoScreen = (e) => {
        if (extra.fraudOnfidoTooManyAttempts) {
            return;
        }
        e.preventDefault();
        if (extra.recipientSumsubEnabled && extra?.recipientAntiFraudStatus?.url != null) {
            props.toggleSpinner(true);
            window.location.href = extra.recipientAntiFraudStatus?.url;
            return;
        }

        if (extra.docs) {
            setScreen(ONFIDO_DOCUMENTS_SCREEN);
        }
    };

    const componentMap = {
        [INTRO_SCREEN]: (
            <CommonPagePromo>
                <PromoInfoPage
                    insideModalComponent={true}
                    iconName="id-card-thin"
                    iconColor="purple"
                    headerText={<LightDangerouslyHtml dangerouslyHtml={messages.introHeaderTitle} />}
                    secondaryText={<LightDangerouslyHtml dangerouslyHtml={messages.introText} />}
                    primaryButtonCaption={messages.continue}
                    primaryButtonType="link"
                    primaryButtonLink={'#'}
                    primaryButtonOnClick={onClickContinueOnIntro}
                />
            </CommonPagePromo>
        ),
        [AGREEMENT_SCREEN]: (
            <CommonPagePromo backUrl={'/'} onClickIcon={onClickBackIcon}>
                <PromoInfoPage
                    insideModalComponent={true}
                    diskButtonOnClick={onBackIconClick}
                    headerText={messages.disclaimerTitle}
                    secondaryText={<LightDangerouslyHtml dangerouslyHtml={messages.disclaimerText} />}
                    buttonRowCaption={messages.hrefLabel}
                    buttonRowType="link"
                    buttonRowHref={extra.agreementHref}
                    primaryButtonType="link"
                    primaryButtonCaption={messages.acceptButton}
                    primaryButtonOnClick={agreementButtonClick}
                />
            </CommonPagePromo>
        ),
        [EMAIL_SCREEN]: (
            <CommonPagePromo onClickIcon={onClickBackIcon} backUrl={'/'}>
                <LightHeaderPanel title={messages.headerEmailScreenTitle} subtitle={messages.headerEmailScreenSubTitle} />
                <Form formName={'recipientEmail'} field={form?.fields} action={'/antifraud/recipient/check.action'} onSubmitHandler={submitEmailHandler}>
                    <PromoInputText field={form?.fields?.email} formName={'recipientEmail'} />
                    <LightFixedBottomPanel>
                        <LightButton type="submit" raised={true} caption={messages.continue} />
                    </LightFixedBottomPanel>
                </Form>
            </CommonPagePromo>
        ),
        [ONFIDO_DOCUMENTS_SCREEN]: (
            <CommonPagePromo backUrl={LANDING_LINK} leftCloseIcon onClickIcon={onClickBackIcon}>
                <LightHeaderPanel title={messages.onfidoTitleWithParams} />
                <LightOnfidoDocList
                    acceptableDocuments={extra.docs?.acceptableDocuments}
                    onClick={onClickDocument}
                    userCountryId={extra.paymentInfo?.recipientCountry}
                    userCountryInEEA={extra.countryInEEA}
                />
                <LightButton
                    variant="row"
                    onClick={(e) => {
                        e.preventDefault();
                        props.toggleChat(true);
                    }}
                    caption={messages.needHelpWithDocument}
                    negativeMargin
                />
            </CommonPagePromo>
        ),
        [ONFIDO_DOCUMENT_EXPLANATION_SCREEN]: (
            <CommonPagePromo backUrl={LANDING_LINK} onClickIcon={onClickBackIcon}>
                <LightHeaderPanel title={messages.suggestionTitle} subtitle={messages.suggestionSubtitle} />
                <LightMarkedList>
                    {extra.suggestionItems?.map((item, index) => (
                        <LightMarkedListItem text={item} key={index} />
                    ))}
                </LightMarkedList>
                <LightFixedBottomPanel>
                    <LightButton
                        type="submit"
                        raised={true}
                        caption={messages.suggestionReady}
                        onClick={() => {
                            openOnfido();
                        }}
                    />
                </LightFixedBottomPanel>
            </CommonPagePromo>
        ),
        [ONFIDO_SDK]: (
            <CommonPagePromo backUrl={LANDING_LINK} onClickIcon={onClickBackIcon}>
                {' '}
                <OnfidoContainer id="onfido-mount" />{' '}
            </CommonPagePromo>
        ),
        [KYC_VERIFYING_SCREEN]: (
            <PromoInfoPage
                iconName="clock-thin"
                iconColor="purple"
                headerText={messages.verifyScreenTitle}
                secondaryText={<LightDangerouslyHtml dangerouslyHtml={messages.verifyScreenText} />}
                primaryButtonCaption={messages.gotItButton}
                primaryButtonType="link"
                primaryButtonLink={LANDING_LINK}
                primaryButtonOnClick={() => {
                    amplitude.getInstance().logEvent(AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });
                }}
            />
        ),
        [KYC_FAILED_SCREEN]: (
            <CommonPagePromo>
                <PromoInfoPage
                    iconName={extra.fraudOnfidoTooManyAttempts ? 'id-card-thin-search' : 'cross-id-card-thin'}
                    iconColor="purple"
                    headerText={<LightDangerouslyHtml dangerouslyHtml={messages.onfidoErrorTitle} />}
                    secondaryText={<LightDangerouslyHtml dangerouslyHtml={messages.onfidoErrorText} />}
                    buttonRowCaption={extra.fraudOnfidoTooManyAttempts ? messages.chatWithSupportText : messages.needHelpWithDocument}
                    buttonRowOnClick={openChat}
                    buttonRowType="link"
                    primaryButtonCaption={messages.agreeButton}
                    primaryButtonType="link"
                    primaryButtonOnClick={clickOnFailedOnfidoScreen}
                    primaryButtonLink={LANDING_LINK}
                />
            </CommonPagePromo>
        ),
        [EXPIRED_SCREEN]: (
            <CommonPagePromo>
                <PromoInfoPage
                    iconName="alert-rounded-thin"
                    iconColor="purple"
                    headerText={<LightDangerouslyHtml dangerouslyHtml={messages.expiredTitle} />}
                    secondaryText={messages.expiredText}
                    primaryButtonCaption={messages.chatButton}
                    primaryButtonType="link"
                    primaryButtonLink={LANDING_LINK}
                    primaryButtonOnClick={openChat}
                />
            </CommonPagePromo>
        ),
    };

    return <>{screen ? componentMap[screen] : null}</>;
};
export default connect(null, {
    updatePageMessages,
    setExtra,
    addExtras,
    toggleSpinner,
    toggleChat,
    toggleModalWithTimer,
})(AntiFraudRecipientFlowPage);
